import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Plus, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { invoicesService } from '@/services/invoicesService'
import { financeService } from '@/services/financeService'
import { itemService } from '@/services/itemService'
import {
  Invoice,
  InvoiceLine,
  InvoiceBonusLine,
  FinanceCustomer,
  Project,
  TaxRate,
  PaymentTerm,
  Currency,
  InvoiceListItem,
  formatItemDisplayLabel,
  type ItemEntity,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi, addCalendarDays } from '@/lib/calendarDate'

function findSarCurrencyId(currencies: Currency[]): number {
  const match = currencies.find((currency) => {
    const code = String(currency.currencyCode ?? '').toUpperCase()
    const name = [
      currency.currencyNameEn,
      currency.currencyNameAr,
      currency.lookupNameEn,
      currency.lookupNameAr,
    ]
      .filter(Boolean)
      .join(' ')
      .toUpperCase()
    return code === 'SAR' || name.includes('SAR') || name.includes('SAUDI') || name.includes('RIYAL')
  })
  return Number(match?.currencyId ?? match?.id ?? currencies[0]?.currencyId ?? currencies[0]?.id ?? 0)
}

export const InvoicesDetail: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const invoiceFromList = (location.state as any)?.invoice as InvoiceListItem | undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: TaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => t.taxRateId === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  const getDefaultRows = (taxRates: TaxRate[] = []): InvoiceLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 3 }, (): InvoiceLine => ({
      lineId: 0,
      invoiceId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxPercent: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      uom: 0,
      companyId: companyId,
      branchId: branchId || 0,
    }))
  }

  const [invoice, setInvoice] = useState<Invoice>({
    invoiceId: 0,
    invoiceDate: calendarToday(),
    invoiceNumber: '',
    customerId: 0,
    dueDate: '',
    supplyDate: '',
    currency: 0,
    conversionRate: 1,
    taxRateId: 0,
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    total: 0,
    note: '',
    companyId: companyId,
    branchId: branchId || user?.selectedBranch?.branchId || 0,
    projectId: 0,
    paymentTermId: 0,
    category: 0,
    poNumber: '',
    refNumber: '',
    retentionPercent: 0,
    totalRetentionAmount: 0,
    invoiceLines: getDefaultRows([]),
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<FinanceCustomer | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [items, setItems] = useState<ItemEntity[]>([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())
  const [bonusLines, setBonusLines] = useState<InvoiceBonusLine[]>([])

  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [
          customersData,
          taxRatesData,
          paymentTermsData,
          currenciesData,
          itemsRes,
        ] = await Promise.all([
          financeService.getAllCustomers(branchId),
          financeService.getTaxRates(branchId),
          financeService.getPaymentTerms(),
          financeService.getCurrencies(),
          itemService.getAllItems(branchId, 1, 2000).catch(() => ({ items: [] as ItemEntity[] })),
        ])

        setCustomers(customersData)
        setTaxRates(taxRatesData)
        setPaymentTerms(paymentTermsData)
        setCurrencies(currenciesData)
        setItems(itemsRes.items || [])

        const sarId = findSarCurrencyId(currenciesData)
        setInvoice((prev) => ({
          ...prev,
          currency: sarId || prev.currency || 0,
          conversionRate: 1,
        }))
      } catch (error: any) {
        console.error('Failed to load invoice lookups', error)
        await showError('Error', error.response?.data?.message || 'Failed to load invoice lookups')
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setInvoice(prev => {
      const hasRowsWithoutVAT = prev.invoiceLines?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.invoiceLines?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, invoiceLines: updatedLines }
    })
  }, [taxRates])

  useEffect(() => {
    if (!isEditMode || !branchId || !id || customers.length === 0) return

    const loadInvoice = async () => {
      try {
        setLoading(true)
        setBonusLines([])
        const invoiceId = Number(id)
        let invoiceLines: InvoiceLine[] = []
        let invoiceHeaderData: Partial<Invoice> = {}

        // Load invoice header from API
        try {
          const headerData = await invoicesService.getInvoiceById(branchId, invoiceId)
          
          const formatDateForInput = (dateString?: string) => calendarFromApi(dateString)

          invoiceHeaderData = {
            invoiceId: headerData.invoiceId || invoiceId,
            invoiceNumber: headerData.invoiceNumber || '',
            invoiceDate: formatDateForInput(headerData.invoiceDate) || calendarToday(),
            customerId: headerData.customerId || 0,
            dueDate: formatDateForInput(headerData.dueDate) || '',
            supplyDate: formatDateForInput(headerData.supplyDate) || '',
            currency: 0,
            conversionRate: 1,
            taxRateId: headerData.taxRateId || 0,
            subTotal: headerData.subTotal || 0,
            totalTax: headerData.totalTax || 0,
            totalDiscount: headerData.totalDiscount || 0,
            total: headerData.total || 0,
            note: headerData.note || '',
            refNumber: headerData.refNumber || '',
            projectId: headerData.projectId || 0,
            paymentTermId: headerData.paymentTermId || 0,
            category: headerData.category || 0,
            poNumber: headerData.poNumber || '',
            invStatus: headerData.invStatus,
            isPhase2: headerData.isPhase2,
            invoiceType: headerData.invoiceType,
            isPosted: headerData.isPosted,
          }
        } catch (headerError: any) {
          console.warn('Failed to load invoice header:', headerError)
          // Fallback to invoiceFromList if API fails
          if (invoiceFromList) {
            invoiceHeaderData = {
              invoiceId: invoiceFromList.invoiceId || invoiceFromList.id || invoiceId,
              invoiceNumber: invoiceFromList.invoiceNumber || invoiceFromList.invoiceNo || '',
              invoiceDate: calendarFromApi(invoiceFromList.invoiceDate) || calendarToday(),
              customerId: invoiceFromList.customerId || 0,
              projectId: invoiceFromList.projectId || 0,
              total: invoiceFromList.total || invoiceFromList.amount || 0,
              refNumber: invoiceFromList.refNumber || invoiceFromList.refNo || '',
              poNumber: invoiceFromList.poNumber || '',
            }
          } else {
            invoiceHeaderData = {
              invoiceId,
              invoiceNumber: '',
              invoiceDate: calendarToday(),
            }
          }
        }

        // Load invoice lines
        try {
          console.log('🟢 [Invoice Detail] Loading invoice lines...', { branchId, invoiceId })
          invoiceLines = await invoicesService.getInvoiceLinesById(branchId, invoiceId)
          console.log('🟢 [Invoice Detail] Invoice lines loaded:', invoiceLines)
          console.log('🟢 [Invoice Detail] Invoice lines count:', invoiceLines.length)
        } catch (linesError: any) {
          console.error('❌ [Invoice Detail] Failed to load invoice lines:', linesError)
          console.error('❌ [Invoice Detail] Error response:', linesError.response)
          console.error('❌ [Invoice Detail] Error message:', linesError.message)
          await showError('Error', `Failed to load invoice lines: ${linesError.response?.data?.message || linesError.message || 'Unknown error'}`)
          invoiceLines = [] // Set empty array on error
        }

        const loadedBonus = await invoicesService.getInvoiceBonusLinesByInvoiceId(invoiceId).catch(() => [])
        setBonusLines(loadedBonus)

        const updatedInvoiceLines =
          invoiceLines.length > 0
            ? invoiceLines.map((line) => ({
                ...calculateLineTotals({
                  ...line,
                  qty: line.qty || 0,
                  unitPrice: line.unitPrice || 0,
                  itemPrice: line.itemPrice || 0,
                  discountPercent: line.discountPercent || 0,
                  discountAmount: line.discountAmount || 0,
                  taxableAmount: line.taxableAmount || 0,
                  taxAmount: line.taxAmount || 0,
                  lineAmount: line.lineAmount || 0,
                }),
              }))
            : getDefaultRows(taxRates)

        setInvoice((prev) => ({
          ...prev,
          ...invoiceHeaderData,
          branchId: branchId,
          currency: findSarCurrencyId(currencies) || prev.currency || 0,
          conversionRate: 1,
          retentionPercent: invoiceHeaderData.retentionPercent || 0,
          totalRetentionAmount: invoiceHeaderData.totalRetentionAmount || 0,
          invoiceLines: updatedInvoiceLines,
        }))

        setDeletedRowIndices(new Set())

        if (invoiceHeaderData.customerId) {
          const customer = customers.find((c) => c.customerId === invoiceHeaderData.customerId)
          if (customer) {
            setSelectedCustomer(customer)
            loadProjects(customer.customerId)
          } else {
            await showError('Error', t('invoices.customerNotFound'))
          }
        }
      } catch (error: any) {
        console.error('Failed to load invoice data', error)
        await showError('Error', error.message || 'Failed to load invoice data')
      } finally {
        setLoading(false)
      }
    }

    loadInvoice()
  }, [isEditMode, branchId, id, customers, invoiceFromList])

  const loadProjects = async (customerId: number) => {
    if (!branchId || !customerId) {
      setProjects([])
      return
    }

    try {
      const data = await financeService.getProjects(branchId, customerId)
      setProjects(data)
    } catch (error: any) {
      console.error('Failed to load projects:', error)
      await showError('Error', error.response?.data?.message || 'Failed to load projects')
    }
  }

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find((c) => c.customerId === Number(customerId))
    if (customer) {
      setSelectedCustomer(customer)
      setInvoice((prev) => ({
        ...prev,
        customerId: customer.customerId,
        projectId: 0,
      }))
      loadProjects(customer.customerId)
    }
  }

  const calculateLineTotals = (line: InvoiceLine): InvoiceLine => {
    const qty = line.qty || 0
    const unitPrice = line.unitPrice || 0
    const discountPercent = line.discountPercent || 0
    const taxPercent = line.taxPercent || line.taxRate || 0

    const itemPrice = qty * unitPrice
    const discountAmount = itemPrice * (discountPercent / 100)
    const taxableAmount = itemPrice - discountAmount
    const taxAmount = taxableAmount * (taxPercent / 100)
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountAmount,
      taxableAmount,
      taxAmount,
      lineAmount,
    }
  }

  const isFormValid = useMemo(() => {
    if (!invoice.customerId) return false
    if (!invoice.invoiceLines || invoice.invoiceLines.length === 0) return false

    const validLines = invoice.invoiceLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      if (!line.description || line.description.trim() === '') return false
      if (!line.qty || line.qty <= 0) return false
      if (!line.taxRateId || line.taxRateId <= 0) return false
      return true
    })

    return validLines.length > 0
  }, [invoice.customerId, invoice.invoiceLines, deletedRowIndices])

  const isInventoryInvoice = useMemo(
    () =>
      Number(invoice.invoiceType) === 2 ||
      (invoice.invoiceLines || []).some((line) => Number(line.itemId) > 0),
    [invoice.invoiceLines, invoice.invoiceType],
  )

  const isPostedInvoice =
    invoice.invStatus === 'Posted' ||
    invoice.invStatus === 'Sent' ||
    invoice.isPosted === true

  const formLocked =
    isEditMode && (isPostedInvoice || (isInventoryInvoice && invoice.isPhase2 === true))

  const itemById = useMemo(() => {
    const map = new Map<number, ItemEntity>()
    items.forEach((it) => {
      if (it.itemId > 0) map.set(it.itemId, it)
    })
    return map
  }, [items])

  const summaryTotals = useMemo(() => {
    const validLines = invoice.invoiceLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    
    // Calculate retention amount from subTotal
    const retentionPercent = invoice.retentionPercent || 0
    const totalRetentionAmount = subTotal * (retentionPercent / 100)
    
    // Total = SubTotal - Discount + VAT - Retention
    const total = subTotal - totalDiscount + totalTax - totalRetentionAmount

    return {
      subTotal,
      totalDiscount,
      taxableAmount,
      totalTax,
      totalRetentionAmount,
      total,
    }
  }, [invoice.invoiceLines, invoice.retentionPercent, deletedRowIndices])

  useEffect(() => {
    setInvoice((prev) => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalDiscount: summaryTotals.totalDiscount,
      totalTax: summaryTotals.totalTax,
      totalRetentionAmount: summaryTotals.totalRetentionAmount,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  const handleLineChange = (index: number, field: keyof InvoiceLine, value: any) => {
    if (formLocked) return
    setInvoice((prev) => {
      const newLines = [...(prev.invoiceLines || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      if (field === 'taxRateId' && value) {
        const taxRate = taxRates.find((t) => t.taxRateId === Number(value))
        if (taxRate) {
          const rateValue = taxRate.taxPercent || taxRate.taxRate || 0
          updatedLine.taxRate = rateValue
          updatedLine.taxPercent = rateValue
        }
      }

      newLines[index] = calculateLineTotals(updatedLine)
      return { ...prev, invoiceLines: newLines }
    })
  }

  const handleAddRows = (count: number) => {
    if (isInventoryInvoice) return
    setInvoice((prev) => {
      const defaultTax = getDefaultTaxRate(taxRates)
      if (defaultTax) {
        console.log('Adding rows with default VAT:', defaultTax)
      } else {
        console.warn('No default tax rate found when adding rows. TaxRates:', taxRates)
      }
      const newLines = Array.from({ length: count }, (): InvoiceLine => ({
        lineId: 0,
        invoiceId: prev.invoiceId || 0,
        itemId: 0,
        description: '',
        qty: 0,
        unitPrice: 0,
        itemPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxRateId: defaultTax?.taxRateId || 0,
        taxRate: defaultTax?.taxRate || 0,
        taxPercent: defaultTax?.taxRate || 0,
        taxAmount: 0,
        lineAmount: 0,
        uom: 0,
        companyId: companyId,
        branchId: branchId || 0,
      }))
      return { ...prev, invoiceLines: [...(prev.invoiceLines || []), ...newLines] }
    })
  }

  const handleDeleteRow = (index: number) => {
    if (formLocked) return
    const line = invoice.invoiceLines?.[index]
    if (line?.lineId && line.lineId > 0) {
      setDeletedRowIndices((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      setInvoice((prev) => {
        const newLines = [...(prev.invoiceLines || [])]
        newLines.splice(index, 1)
        return { ...prev, invoiceLines: newLines }
      })
    }
  }

  /**
   * Get user IP address (logged-in user's IP, not server's IP)
   */
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (formLocked) {
      await showValidationError(t('invoices.inventoryInvoiceCannotBeEdited'))
      return
    }

    if (!branchId) {
      await showValidationError(t('invoices.pleaseSelectBranch'))
      return
    }

    if (!invoice.customerId) {
      await showValidationError(t('invoices.pleaseSelectCustomer'))
      return
    }

    if (!invoice.invoiceLines || invoice.invoiceLines.length === 0) {
      await showValidationError(t('invoices.pleaseAddAtLeastOneItem'))
      return
    }

    const validLines = invoice.invoiceLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      return !!(
        line.description?.trim() &&
        line.qty > 0 &&
        line.taxRateId &&
        line.taxRateId > 0
      )
    })

    if (validLines.length === 0) {
      await showValidationError(t('invoices.pleaseAddAtLeastOneValidItem'))
      return
    }

    for (let i = 0; i < validLines.length; i++) {
      const line = validLines[i]
      if (!line.description || line.description.trim() === '') {
        await showValidationError(t('invoices.pleaseEnterDescription', { index: i + 1 }))
        return
      }
      if (!line.qty || line.qty <= 0) {
        await showValidationError(t('invoices.pleaseEnterQuantity', { index: i + 1 }))
        return
      }
      if (!line.taxRateId || line.taxRateId <= 0) {
        await showValidationError(t('invoices.pleaseSelectVat', { index: i + 1 }))
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Invoice...' : 'Saving Invoice...',
        'Please wait while we process your request'
      )
      
      const currentBranchId = user?.selectedBranch?.branchId
      if (!currentBranchId) {
        closeSwal()
        await showValidationError(t('invoices.pleaseSelectBranch'))
        setSaving(false)
        return
      }

      // Get user IP address
      const ipAddress = await getUserIPAddress()
      console.log('IP Address (User):', ipAddress)

      // Prepare invoice lines - filter out empty lines and ensure proper structure
      const linesToSave: InvoiceLine[] = []
      invoice.invoiceLines?.forEach((line, index) => {
        const isDeleted = deletedRowIndices.has(index)

        if (
          !isDeleted &&
          (!line.description?.trim() || !line.qty || line.qty <= 0 || !line.taxRateId || line.taxRateId <= 0)
        ) {
          return
        }

        // Get tax rate value
        let taxRateValue = line.taxRate || line.taxPercent || 0
        if (line.taxRateId && line.taxRateId > 0 && taxRateValue === 0) {
          const taxRate = taxRates.find((t) => t.taxRateId === line.taxRateId)
          if (taxRate) {
            taxRateValue = taxRate.taxPercent || taxRate.taxRate || 0
          }
        }

        // Recalculate line totals
        const recalculatedLine = calculateLineTotals({
          ...line,
          taxRate: taxRateValue,
          taxPercent: taxRateValue,
        })

        // Build line object matching exact API structure
        const lineToSave: InvoiceLine = {
          lineId: recalculatedLine.lineId || 0,
          invoiceId: invoice.invoiceId || 0,
          itemId: recalculatedLine.itemId || 0,
          description: recalculatedLine.description || '',
          qty: recalculatedLine.qty || 0,
          unitPrice: recalculatedLine.unitPrice || 0,
          itemPrice: recalculatedLine.itemPrice || 0,
          discountPercent: recalculatedLine.discountPercent || 0,
          discountAmount: recalculatedLine.discountAmount || 0,
          taxableAmount: recalculatedLine.taxableAmount || 0,
          taxRateId: recalculatedLine.taxRateId || 0,
          taxRate: taxRateValue,
          taxAmount: recalculatedLine.taxAmount || 0,
          lineAmount: recalculatedLine.lineAmount || 0,
          uom: recalculatedLine.uom || 0,
          companyId: companyId || 0,
          branchId: currentBranchId || 0,
          taxPercent: taxRateValue,
          isDeleted: isDeleted ? 'true' : 'false',
        }

        linesToSave.push(lineToSave)
      })

      // Build payload matching exact API structure
      // Helper to ensure date is always ISO string (not undefined)
      const toApiDateRequired = (dateValue?: string): string => calendarToApi(dateValue)
      const payload: Invoice = {
        invoiceId: invoice.invoiceId || 0,
        invoiceDate: toApiDateRequired(invoice.invoiceDate),
        customerId: invoice.customerId || 0,
        dueDate: toApiDateRequired(invoice.dueDate),
        currency: findSarCurrencyId(currencies) || invoice.currency || 0,
        conversionRate: 1,
        subTotal: invoice.subTotal || 0,
        totalTax: invoice.totalTax || 0,
        totalDiscount: invoice.totalDiscount || 0,
        total: invoice.total || 0,
        note: invoice.note || '',
        companyId: companyId || 0,
        branchId: currentBranchId || 0,
        projectId: invoice.projectId || 0,
        paymentTermId: invoice.paymentTermId || 0,
        retentionPercent: invoice.retentionPercent || 0,
        totalRetentionAmount: invoice.totalRetentionAmount || 0,
        category: invoice.category || 0,
        supplyDate: toApiDateRequired(invoice.supplyDate),
        ipAddress: ipAddress || '',
        invoiceLines: linesToSave,
      }

      // Enhanced logging to verify structure
      console.group('🟢 [Invoice Save] Payload Structure')
      console.log('Mode:', isEditMode ? '✏️ UPDATE' : '➕ INSERT')
      console.log('API Endpoint:', isEditMode ? 'PUT /api/ApiInvoice/Update' : 'POST /api/ApiInvoice/Insert')
      console.log('IP Address (User):', ipAddress)
      console.log('')
      console.log('📦 Master Data (Invoice):')
      console.log(JSON.stringify({
        invoiceId: payload.invoiceId,
        invoiceDate: payload.invoiceDate,
        customerId: payload.customerId,
        dueDate: payload.dueDate,
        currency: payload.currency,
        conversionRate: payload.conversionRate,
        subTotal: payload.subTotal,
        totalTax: payload.totalTax,
        totalDiscount: payload.totalDiscount,
        total: payload.total,
        note: payload.note,
        companyId: payload.companyId,
        branchId: payload.branchId,
        projectId: payload.projectId,
        paymentTermId: payload.paymentTermId,
        retentionPercent: payload.retentionPercent,
        totalRetentionAmount: payload.totalRetentionAmount,
        category: payload.category,
        supplyDate: payload.supplyDate,
        ipAddress: payload.ipAddress,
      }, null, 2))
      console.log('')
      console.log('📋 Detail Lines (invoiceLines):')
      console.log('Total Lines:', linesToSave.length)
      console.log(JSON.stringify(linesToSave, null, 2))
      console.log('')
      console.log('📦 Full Payload (Complete Structure):')
      console.log(JSON.stringify(payload, null, 2))
      console.groupEnd()

      if (isEditMode) {
        await invoicesService.updateInvoice(payload)
      } else {
        await invoicesService.insertInvoice(payload)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('invoices.invoiceUpdatedSuccess') : t('invoices.invoiceCreatedSuccess')
      )

      navigate('/invoices')
    } catch (error: any) {
      console.error('Failed to save invoice', error)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const formatDateForInput = (dateString?: string) => calendarFromApi(dateString)

  const customerOptions: SearchableSelectOption[] = customers.map((customer) => ({
    value: String(customer.customerId),
    label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
  }))

  const taxRateOptions: SearchableSelectOption[] = taxRates.map((tax) => ({
    value: String(tax.taxRateId),
    label: `${tax.taxNameEn || tax.taxNameAr || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
  }))

  const taxRateOptionsWithNone: SearchableSelectOption[] = useMemo(() => {
    // Backend may store "no VAT" as -1. UI expects a selectable "0" option.
    return [{ value: '0', label: t('invoices.vatNone', { defaultValue: 'VAT (0%)' }) }, ...taxRateOptions]
  }, [taxRateOptions, t])

  const paymentTermOptions: SearchableSelectOption[] = paymentTerms.map((term) => ({
    value: String(term.id),
    label: term.lookupNameEn || term.lookupNameAr || term.paymentTermNameEn || term.paymentTermNameAr || String(term.id),
  }))

  const projectOptions: SearchableSelectOption[] = [
    { value: '0', label: 'None' },
    ...projects
      .filter((project) => {
        const actualId = project.id ?? project.projectId
        return actualId && actualId !== 0
      })
      .map((project) => {
        const actualId = project.id ?? project.projectId ?? 0
        const projectName =
          project.name ||
          project.projectNameEn ||
          project.projectNameAr ||
          project.projectName ||
          project.nameEn ||
          project.nameAr ||
          `Project ${actualId}`

        return {
          value: String(actualId),
          label: projectName.trim(),
        }
      }),
  ]

  if (loading && !invoice.invoiceLines?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('navigation.invoices')}
        description={
          formLocked
            ? t('invoices.inventoryInvoiceCannotBeEdited')
            : isEditMode
              ? t('invoices.updateInvoiceDetails')
              : t('invoices.createNewInvoice')
        }
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>{t('invoices.branch')}</Label>
              <Input value={user?.selectedBranch?.branchName || 'N/A'} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.invoiceTo')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={invoice.customerId ? String(invoice.customerId) : ''}
                onValueChange={handleCustomerChange}
                placeholder={t('invoices.selectCustomer')}
                searchPlaceholder={t('invoices.searchCustomers')}
                emptyMessage={t('invoices.noCustomersFound')}
                disabled={formLocked}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t('invoices.project')}</Label>
              <SearchableSelect
                options={projectOptions}
                value={invoice.projectId ? String(invoice.projectId) : '0'}
                onValueChange={(value) => setInvoice((prev) => ({ ...prev, projectId: Number(value) || 0 }))}
                placeholder={t('invoices.selectProject')}
                searchPlaceholder={t('invoices.searchProjects')}
                emptyMessage={t('invoices.noProjectsFound')}
                disabled={!invoice.customerId || formLocked}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedCustomer && (
        <Card>
          <CardHeader>
            <CardTitle>{t('invoices.clientDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{t('invoices.nameEn')}</Label>
                <Input value={selectedCustomer.customerNameEn || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.nameAr')}</Label>
                <Input value={selectedCustomer.customerNameAr || ''} readOnly className="bg-muted text-right" dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.email')}</Label>
                <Input value={selectedCustomer.email || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.phone')}</Label>
                <Input value={selectedCustomer.phone || selectedCustomer.phoneNo || selectedCustomer.mobileNo || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.companyRegistrationNumber')}</Label>
                <Input value={selectedCustomer.companyRegistrationNumber || selectedCustomer.companyRegistrationNo || selectedCustomer.companyRegNumber || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.vatNumber')}</Label>
                <Input value={selectedCustomer.vatNumber || selectedCustomer.vatNo || '0'} readOnly className="bg-muted" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t('invoices.invoiceDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(invoice.invoiceDate)}
                onChange={(e) => setInvoice((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                disabled={formLocked}
              />
            </div>
            {isEditMode && (
              <div className="space-y-2">
                <Label>{t('invoices.invoiceNumber')}</Label>
                <Input value={invoice.invoiceNumber || ''} readOnly className="bg-muted" placeholder={t('invoices.autoGenerated')} />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('invoices.poNumber')}</Label>
              <Input
                value={invoice.poNumber || ''}
                onChange={(e) => setInvoice((prev) => ({ ...prev, poNumber: e.target.value }))}
                placeholder={t('invoices.poNumberPlaceholder')}
                disabled={formLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.refNumber')}</Label>
              <Input
                value={invoice.refNumber || ''}
                onChange={(e) => setInvoice((prev) => ({ ...prev, refNumber: e.target.value }))}
                placeholder={t('invoices.refNumberPlaceholder')}
                disabled={formLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.paymentTerm')}</Label>
              <SearchableSelect
                options={paymentTermOptions}
                value={invoice.paymentTermId ? String(invoice.paymentTermId) : ''}
                onValueChange={(value) => {
                  const termId = Number(value) || 0
                  const selectedTerm = paymentTerms.find(term => term.id === termId)
                  const termLabel = selectedTerm?.lookupNameEn || selectedTerm?.lookupNameAr || ''
                  
                  // Calculate supply date based on payment term
                  let newSupplyDate = ''
                  const invoiceDate = invoice.invoiceDate || calendarToday()
                  
                  if (termLabel.toLowerCase() === 'advance' || termLabel.toLowerCase() === 'due on receipt') {
                    // For Advance or Due on Receipt, set supply date to invoice date
                    newSupplyDate = invoiceDate
                  } else if (termLabel.toLowerCase().startsWith('net ')) {
                    // For Net X, parse the number and add days
                    const match = termLabel.match(/net\s+(\d+)/i)
                    if (match && match[1]) {
                      const days = parseInt(match[1], 10)
                      if (!isNaN(days)) {
                        newSupplyDate = addCalendarDays(invoiceDate, days)
                      }
                    }
                  }
                  
                  setInvoice((prev) => ({ 
                    ...prev, 
                    paymentTermId: termId,
                    supplyDate: newSupplyDate || prev.supplyDate
                  }))
                }}
                placeholder={t('invoices.selectPaymentTerm')}
                searchPlaceholder={t('invoices.searchPaymentTerms')}
                emptyMessage={t('invoices.noPaymentTermsFound')}
                disabled={formLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.dueDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(invoice.dueDate)}
                onChange={(e) => setInvoice((prev) => ({ ...prev, dueDate: e.target.value }))}
                disabled={formLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.supplyDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(invoice.supplyDate)}
                onChange={(e) => setInvoice((prev) => ({ ...prev, supplyDate: e.target.value }))}
                disabled={formLocked}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-primary/5">
          <div className="flex items-center justify-between">
            <CardTitle>{t('invoices.items')}</CardTitle>
            {!isInventoryInvoice && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleAddRows(1)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('invoices.addRow')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(3)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('invoices.add3Rows')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(5)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('invoices.add5Rows')}
              </Button>
            </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  {isInventoryInvoice && (
                    <th className="text-left p-3 font-semibold align-middle">{t('invoices.item')}</th>
                  )}
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.description')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.qty')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.unitPrice')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.itemPrice')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.discPercent')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.discAmount')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.taxable')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.vat')} <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.vatAmount')}</th>
                  <th className="text-left p-3 font-semibold align-middle">{t('invoices.lineAmount')}</th>
                  <th className="text-left p-3 font-semibold align-middle w-20"></th>
                </tr>
              </thead>
              <tbody>
                {invoice.invoiceLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  const itemNum = Number(line.itemId || 0)
                  const itemLocked = isInventoryInvoice && itemNum > 0
                  const lineBonuses = bonusLines.filter((b) => b.invoiceLineId === Number(line.lineId || 0))
                  return (
                    <React.Fragment key={index}>
                    <tr
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      {isInventoryInvoice && (
                        <td className="p-3 align-middle">
                          <div className="flex items-center h-10">
                            <Input
                              readOnly
                              tabIndex={-1}
                              className="min-w-[180px] cursor-default bg-muted"
                              value={
                                itemNum > 0
                                  ? formatItemDisplayLabel(itemById.get(itemNum), i18n.language, itemNum)
                                  : ''
                              }
                            />
                          </div>
                        </td>
                      )}
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            placeholder={t('invoices.description')}
                            disabled={saving || isDeleted || formLocked}
                            className="min-w-[200px]"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted || formLocked}
                            className="w-20"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted || formLocked}
                            className="w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.itemPrice?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.discountPercent || ''}
                          onChange={(e) => handleLineChange(index, 'discountPercent', Number(e.target.value) || 0)}
                          placeholder="0.00"
                          disabled={saving || isDeleted || formLocked}
                          className="w-20"
                        />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.discountAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxableAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptionsWithNone}
                            value={Number(line.taxRateId ?? 0) > 0 ? String(line.taxRateId) : '0'}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value))}
                            placeholder={t('invoices.selectVat')}
                            searchPlaceholder={t('invoices.searchVat')}
                            emptyMessage={t('invoices.noVatRatesFound')}
                            disabled={saving || isDeleted || formLocked}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.lineAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving || itemLocked || formLocked}
                          className={
                            isDeleted
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                              : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                          }
                          title={
                            itemLocked
                              ? t('invoices.item')
                              : isDeleted
                                ? t('invoices.undo')
                                : t('invoices.deleteRow')
                          }
                        >
                          {isDeleted ? <span className="text-xs font-semibold">{t('invoices.undo')}</span> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                    {lineBonuses.map((b) => {
                      const bonusName =
                        b.itemName ||
                        formatItemDisplayLabel(itemById.get(b.itemId), i18n.language, b.itemId)
                      return (
                        <tr key={`bonus-${b.id || b.itemId}`} className="border-b bg-muted/40">
                          {isInventoryInvoice && (
                            <td className="p-3 align-middle">
                              <Input
                                readOnly
                                className="min-w-[180px] cursor-default bg-muted"
                                value={`${t('invoices.bonus')}: ${bonusName}`}
                              />
                            </td>
                          )}
                          <td className="p-3 align-middle">
                            <Input readOnly className="min-w-[200px] bg-muted" value={t('invoices.bonus')} />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-20 bg-muted" value={b.quantity} />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-20 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="bg-muted" value="" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="w-24 bg-muted" value="0.00" />
                          </td>
                          <td className="p-3 align-middle" />
                        </tr>
                      )
                    })}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.subTotal')}</Label>
                <p className="text-lg font-semibold">{summaryTotals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.totalDisc')}</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalDiscount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.taxableAmount')}</Label>
                <p className="text-lg font-semibold">{summaryTotals.taxableAmount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.totalVat')}</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.retentionPercent')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoice.retentionPercent || ''}
                  onChange={(e) => {
                    const retentionPercent = Number(e.target.value) || 0
                    setInvoice(prev => ({ ...prev, retentionPercent }))
                  }}
                  placeholder="0"
                  disabled={saving || formLocked}
                  className="w-full mt-1"
                />
                <p className="text-sm font-semibold mt-1">{summaryTotals.totalRetentionAmount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('invoices.total')}</Label>
                <p className="text-lg font-semibold">{summaryTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.notesRemarks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={invoice.note || ''}
            onChange={(e) => setInvoice((prev) => ({ ...prev, note: e.target.value }))}
            placeholder={t('invoices.enterNotesRemarks')}
            className="min-h-[100px]"
            disabled={formLocked}
          />
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/invoices')} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        {!formLocked && (
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || !isFormValid}
          className="w-full sm:w-auto min-w-[150px]"
          title={!isFormValid ? t('invoices.pleaseFillRequiredFields') : ''}
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('invoices.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('invoices.updateInvoice') : t('invoices.saveInvoice')}
            </>
          )}
        </Button>
        )}
      </div>
    </div>
  )
}
